import React, { useCallback, useState, useContext, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthContext from "../../contexts/AuthProvider";

const getLangFromLocalStorage = () => localStorage.getItem("language") || "en";

function EditAttraction({ baseUrl, venueId, attraction, setEditAttraction, onSaved }) {
  const { t } = useTranslation("venue");
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);

  const isEdit = Boolean(attraction?.id);

  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [website, setWebsite] = useState('');
  const [file, setFile] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState('');
  const [Errors, setErrors] = useState(null);
  const [showValidation, setShowValidation] = useState(false);

  useEffect(() => {
    if (attraction) {
      setName(attraction.name ?? '');
      setDesc(attraction.description ?? '');
      setWebsite(attraction.url ?? '');
      setExistingImageUrl(attraction.image ?? '');
    } else {
      setName('');
      setDesc('');
      setWebsite('');
      setExistingImageUrl('');
      setFile(null);
    }
  }, [attraction]);

  const handleFileChange = () => (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
      const allowedExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp'];
      const isValidMimeType = allowedTypes.includes(selectedFile.type);
      const fileName = selectedFile.name.toLowerCase();
      const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
      if (isValidMimeType && isValidExtension) {
        setFile(selectedFile);
        setShowValidation(false);
      } else {
        setFile(null);
        setShowValidation(true);
      }
    } else {
      setFile(null);
    }
  };

  const onDropLogo = useCallback(
    (acceptedFiles, rejectedFiles) => {
      if (acceptedFiles.length > 0) {
        handleFileChange()({ target: { files: acceptedFiles } });
      } else if (rejectedFiles.length > 0) {
        setFile(null);
        setShowValidation(true);
      }
    },
    []
  );

  const { getRootProps: getRootPropsLogo } = useDropzone({
    onDrop: onDropLogo,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
    },
  });

  const isValidName = () => name && name.trim().length > 2;
  // Website is not required, so don't validate unless there's a value.
  // Also update canSubmit logic accordingly.
  const isWebsite = () => {
    if (!website) return true;
    const websiteRegex = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}\/?$/;
    return websiteRegex.test(website);
  };
  const isValidFile = () => {
    if (!file) return false;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    const fileName = file.name.toLowerCase();
    const allowedExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp'];
    return allowedTypes.includes(file.type) && allowedExtensions.some(ext => fileName.endsWith(ext));
  };
  const hasImage = () => file || (isEdit && existingImageUrl);

  // Remove isWebsite from required canSubmit checks
  const canSubmit = () =>
    isValidName() &&
    desc &&
    desc.trim().length >= 10 &&
    (isEdit ? true : isValidFile()) &&
    isWebsite();

  const updateAttraction = async () => {
    setShowValidation(true);
    if (!canSubmit()) return;

    const loadingToast = toast.loading(t('toast.processingEdit'));
    try {
      if (!isAuthenticated) {
        throw new Error(t('errors.noToken'));
      }

      const formData = new FormData();
      formData.append('attraction_id', attraction.id);
      formData.append('name', name);
      formData.append('description', desc);
      // Always send url so clearing the field removes it server-side
      formData.append('url', (website || '').trim());
      if (file) formData.append('image', file);

      const url = `${baseUrl}/api/v1/attnd-venue/updateLocalAttraction`;
      const response = await fetch(url, {
        method: 'PUT',
        credentials: 'include',
        headers: { lang: getLangFromLocalStorage() },
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 401) {
          navigate('/login');
          throw new Error(t('errors.unauthorized'));
        }
        const error = await response.json();
        setErrors(error);
        const errorMessage =
          error.error ||
          error.errors?.[0]?.name ||
          error.errors?.[0]?.url ||
          error.errors?.[0]?.description ||
          error.errors?.[0]?.image ||
          t('errors.unknown');
        throw new Error(errorMessage);
      }

      await response.json();
      toast.update(loadingToast, {
        render: t('toast.updated'),
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      setEditAttraction(null);
      onSaved?.();
    } catch (error) {
      console.error(error);
      toast.update(loadingToast, {
        render: t('toast.error', { message: error.message }),
        type: 'error',
        isLoading: false,
        autoClose: 5000,
      });
    }
  };

  const addAttraction = async () => {
    setShowValidation(true);
    if (!(isValidName() && desc && desc.trim().length >= 10 && isValidFile() && isWebsite())) return;

    const loadingToast = toast.loading(t('toast.processing'));
    try {
      if (!isAuthenticated) {
        throw new Error(t('errors.noToken'));
      }

      const formData = new FormData();
      formData.append('venue_id', venueId);
      formData.append('name', name);
      // Only append url if there is a non-empty value
      if (website && website.trim() !== '') formData.append('url', website);
      formData.append('description', desc);
      formData.append('image', file);

      const url = `${baseUrl}/api/v1/attnd-venue/addLocalAttraction`;
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { lang: getLangFromLocalStorage() },
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 401) {
          navigate('/login');
          throw new Error(t('errors.unauthorized'));
        }
        const error = await response.json();
        setErrors(error);
        const errorMessage =
          error.error ||
          error.errors?.[0]?.name ||
          error.errors?.[0]?.url ||
          error.errors?.[0]?.description ||
          error.errors?.[0]?.image ||
          t('errors.unknown');
        throw new Error(errorMessage);
      }

      await response.json();
      toast.update(loadingToast, {
        render: t('toast.success'),
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      setEditAttraction(null);
      onSaved?.();
    } catch (error) {
      console.error(error);
      toast.update(loadingToast, {
        render: t('toast.error', { message: error.message }),
        type: 'error',
        isLoading: false,
        autoClose: 5000,
      });
    }
  };

  const handleSubmit = isEdit ? updateAttraction : addAttraction;
  const close = () => setEditAttraction(null);

  return (
    <div className="addAttraction">
      <div className="AttractionContent">
        <h3>{isEdit ? t('editTitle', { defaultValue: 'Edit attraction' }) : t('title')}</h3>

        <div className="AttractionContent-name">
          <h5>{t('fields.siteName')} <span style={{ color: 'red' }}>*</span></h5>
          <div>
            <input
              type="text"
              value={name ?? ''}
              placeholder={t('placeholders.siteName')}
              onChange={(e) => setName(e.target.value)}
              style={
                !Errors?.errors?.[0]?.name && (isValidName() || !name)
                  ? {}
                  : { borderColor: 'red' }
              }
            />
            {(Errors?.errors?.[0]?.name || (showValidation && !isValidName() && name)) && (
              <p style={{ fontSize: '12px', color: 'red' }}>
                {Errors?.errors?.[0]?.name || t('validation.name')}
              </p>
            )}
          </div>
        </div>

        <div className="AttractionContent-name">
          <h5>{t('fields.description')} <span style={{ color: 'red' }}>*</span></h5>
          <div className="description">
            <textarea
              className="desc"
              value={desc ?? ''}
              placeholder={t('placeholders.description')}
              onChange={(e) => setDesc(e.target.value)}
              style={
                !Errors?.errors?.[0]?.description &&
                ((desc && desc.trim().length >= 10) || !desc)
                  ? {}
                  : { borderColor: 'red' }
              }
            />
            {(Errors?.errors?.[0]?.description || (showValidation && (!desc || desc.trim().length < 10))) && (
              <p style={{ fontSize: '12px', color: 'red' }}>
                {Errors?.errors?.[0]?.description || t('validation.description')}
              </p>
            )}
          </div>
        </div>

        <div className="AttractionContent-name">
          <h5>{t('fields.website')}</h5>
          <div>
            <input
              type="text"
              value={website ?? ''}
              placeholder={t('placeholders.website')}
              onChange={(e) => setWebsite(e.target.value)}
              style={
                !Errors?.errors?.[0]?.url && isWebsite()
                  ? {}
                  : { borderColor: 'red', outline: 'red' }
              }
            />
            {
              // Only show validation if website is not empty and invalid,
              // or if server error is present for the field
              (Errors?.errors?.[0]?.url || (showValidation && website && !isWebsite())) && (
                <p style={{ fontSize: '12px', color: 'red' }}>
                  {Errors?.errors?.[0]?.url || t('validation.website')}
                </p>
              )
            }
          </div>
        </div>

        <div className="AttractionContent-name">
          <h5 className="eventPage-form-title">
            {t('fields.uploadPhoto')} {!isEdit && <span style={{ color: 'red' }}>*</span>}
          </h5>
          <label className="photo-input-label" {...getRootPropsLogo()}>
            <div className="photo-input-box">
              <p>
                {!file && !existingImageUrl && <>{t('dropzone.instruction')} </>}
                <span style={!file && !existingImageUrl ? { marginLeft: '4px', textDecoration: 'underline' } : {}}>
                  {file ? file.name : existingImageUrl ? t('currentPhoto', { defaultValue: 'Current photo' }) : t('dropzone.browse')}
                </span>
              </p>
              <input
                style={{ display: 'none' }}
                className="photo-input"
                type="file"
                name="logo"
                accept=".png,.jpg,.jpeg,.gif,.webp"
                onChange={handleFileChange()}
              />
            </div>
            <svg style={{ minWidth: '24px', minHeight: '24px' }} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 1.25H9C3.57 1.25 1.25 3.57 1.25 9V15C1.25 20.43 3.57 22.75 9 22.75H15C20.43 22.75 22.75 20.43 22.75 15V9C22.75 3.57 20.43 1.25 15 1.25ZM9 21.25C4.39 21.25 2.75 19.61 2.75 15V9C2.75 4.39 4.39 2.75 9 2.75H15C19.61 2.75 21.25 4.39 21.25 9V15C21.25 19.61 19.61 21.25 15 21.25H9Z" fill="#5507CF"/>
              <path d="M11.9995 8.73981C11.8095 8.73981 11.6195 8.80981 11.4695 8.95981L8.46945 11.9598C8.17945 12.2498 8.17945 12.7298 8.46945 13.0198C8.75945 13.3098 9.23945 13.3098 9.52945 13.0198L11.9995 10.5498L14.4695 13.0198C14.7595 13.3098 15.2395 13.3098 15.5295 13.0198C15.8195 12.7298 15.8195 12.2498 15.5295 11.9598L12.5295 8.95981C12.3795 8.80981 12.1895 8.73981 11.9995 8.73981Z" fill="#5507CF"/>
              <path d="M12 8.74023C11.59 8.74023 11.25 9.08023 11.25 9.49023V17.4902C11.25 17.9002 11.59 18.2402 12 18.2402C12.41 18.2402 12.75 17.9002 12.75 17.4902V9.49023C12.75 9.07023 12.41 8.74023 12 8.74023Z" fill="#5507CF"/>
              <path d="M12.0004 5.76984C9.89043 5.76984 7.77043 6.10984 5.76043 6.77984C5.37043 6.90984 5.16043 7.33984 5.29043 7.72984C5.42043 8.11984 5.84043 8.33984 6.24043 8.19984C9.96043 6.95984 14.0504 6.95984 17.7704 8.19984C18.1604 8.32984 18.5904 8.11984 18.7204 7.72984C18.8504 7.33984 18.6404 6.90984 18.2504 6.77984C16.2304 6.09984 14.1104 5.76984 12.0004 5.76984Z" fill="#5507CF"/>
            </svg>
          </label>
          {isEdit ? null : (Errors?.errors?.[0]?.image || (showValidation && !isValidFile())) && (
            <p style={{ fontSize: '12px', color: 'red' }}>
              {Errors?.errors?.[0]?.image || t('validation.photo')}
            </p>
          )}
        </div>

        <div className="Buttons">
          <button onClick={close} className="Cancel">
            {t('buttons.cancel')}
          </button>
          <button
            className="confirm"
            onClick={handleSubmit}
            disabled={!canSubmit()}
            style={canSubmit() ? {} : { background: '#888888', cursor: 'not-allowed' }}
          >
            {isEdit ? (t('buttons.update', { defaultValue: 'Update' })) : t('buttons.add')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditAttraction;
